<template>
  <main class="index-page">
    <header class="page-header">
      <h1>模型价格</h1>
      <p>按本地模型名配置额度扣费规则，鼠标悬停在字段上可查看说明。</p>
    </header>

    <el-card class="panel-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>{{ editingItem ? '编辑模型价格' : '新增模型价格' }}</span>
        </div>
      </template>
      <el-form class="form-grid form-grid-4" label-position="top" @submit.prevent>
        <el-form-item label="本地模型名">
          <el-input v-model.trim="form.model" placeholder="fast" />
        </el-form-item>
        <el-form-item label="参考上游模型">
          <el-input v-model.trim="form.upstream_model" placeholder="deepseek-chat" />
        </el-form-item>
        <el-form-item label="Prompt 倍率">
          <el-input-number v-model="form.prompt_ratio" :min="0" :step="0.0001" controls-position="right" />
        </el-form-item>
        <el-form-item label="Completion 倍率">
          <el-input-number v-model="form.completion_ratio" :min="0" :step="0.0001" controls-position="right" />
        </el-form-item>
        <el-form-item label="缓存倍率">
          <el-input-number v-model="form.cache_ratio" :min="0" :step="0.0001" controls-position="right" />
        </el-form-item>
        <el-form-item label="图片倍率">
          <el-input-number v-model="form.image_ratio" :min="0" :step="0.0001" controls-position="right" />
        </el-form-item>
        <el-form-item label="无 usage 兜底扣费">
          <el-input-number v-model="form.fallback_cost" :min="0" controls-position="right" />
        </el-form-item>
        <el-form-item label="备注" class="span-2">
          <el-input v-model.trim="form.remark" placeholder="可写上游价格来源或说明" />
        </el-form-item>
        <el-form-item>
          <div class="switch-row">
            <el-switch v-model="enabled" />
            <span>启用</span>
          </div>
        </el-form-item>
        <el-form-item class="form-actions span-2">
          <el-button type="primary" :loading="saving" @click="save">保存</el-button>
          <el-button v-if="editingItem" @click="cancel">取消</el-button>
        </el-form-item>
      </el-form>
      <el-alert v-if="notice" class="notice-alert" :title="notice" type="success" show-icon :closable="false" />
    </el-card>

    <el-card class="panel-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>价格列表</span>
          <el-tag effect="plain">{{ items.length }} 条</el-tag>
        </div>
      </template>
      <el-table :data="items" v-loading="loading" empty-text="暂无模型价格" stripe>
        <el-table-column label="本地模型" min-width="150">
          <template #default="{ row }"><span class="mono">{{ row.model }}</span></template>
        </el-table-column>
        <el-table-column label="参考上游模型" min-width="170">
          <template #default="{ row }"><span class="mono">{{ row.upstream_model || '-' }}</span></template>
        </el-table-column>
        <el-table-column label="Prompt 倍率" width="130">
          <template #default="{ row }">{{ formatRatio(row.prompt_ratio) }}</template>
        </el-table-column>
        <el-table-column label="Completion 倍率" width="160">
          <template #default="{ row }">{{ formatRatio(row.completion_ratio) }}</template>
        </el-table-column>
        <el-table-column label="缓存倍率" width="130">
          <template #default="{ row }">{{ formatRatio(row.cache_ratio) }}</template>
        </el-table-column>
        <el-table-column label="图片倍率" width="130">
          <template #default="{ row }">{{ formatRatio(row.image_ratio) }}</template>
        </el-table-column>
        <el-table-column prop="fallback_cost" label="Fallback" width="110" />
        <el-table-column label="状态" width="110">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" effect="plain">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="primary" plain @click="edit(row)">编辑</el-button>
            <el-button size="small" type="danger" plain @click="remove(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </main>
</template>

<script>
import { createModelPrice, deleteModelPrice, fetchModelPrices, updateModelPrice } from '@/api/modelPrices'

const defaultForm = () => ({
  model: '',
  upstream_model: '',
  prompt_ratio: 1,
  completion_ratio: 1,
  cache_ratio: 1,
  image_ratio: 1,
  fallback_cost: 1,
  remark: ''
})

export default {
  name: 'ModelPriceView',
  data() {
    return {
      items: [],
      form: defaultForm(),
      editingItem: null,
      enabled: true,
      loading: false,
      saving: false,
      notice: ''
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        this.items = await fetchModelPrices()
      } catch (error) {
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      this.notice = ''
      const data = {
        ...this.form,
        status: this.enabled ? 1 : 0
      }
      try {
        if (this.editingItem) {
          await updateModelPrice(this.editingItem.id, data)
          this.notice = '模型价格已更新'
        } else {
          await createModelPrice(data)
          this.notice = '模型价格已创建'
        }
        this.cancel()
        await this.load()
      } catch (error) {
        this.notice = error.message
      } finally {
        this.saving = false
      }
    },
    edit(item) {
      this.editingItem = item
      this.form = {
        model: item.model,
        upstream_model: item.upstream_model || '',
        prompt_ratio: item.prompt_ratio,
        completion_ratio: item.completion_ratio,
        cache_ratio: item.cache_ratio || 1,
        image_ratio: item.image_ratio || 1,
        fallback_cost: item.fallback_cost,
        remark: item.remark || ''
      }
      this.enabled = item.status === 1
    },
    cancel() {
      this.editingItem = null
      this.form = defaultForm()
      this.enabled = true
    },
    async remove(item) {
      const confirmed = window.confirm(`确定删除模型价格 ${item.model} 吗？`)
      if (!confirmed) {
        return
      }
      try {
        await deleteModelPrice(item.id)
        this.notice = '模型价格已删除'
        await this.load()
      } catch (error) {
        this.notice = error.message
      }
    },
    formatRatio(value) {
      return Number(value || 0).toFixed(4)
    }
  }
}
</script>
